"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Search, Settings, FileText, Sparkles, ChartBar as BarChart3, ArrowRight, CircleCheck as CheckCircle2, UserCheck, ShieldCheck, Compass } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
interface StepItem {
  number: string;
  title: string;
  stage: string;
  description: string;
  deliverables: string;
  iconName: "Search" | "Settings" | "FileText" | "Sparkles" | "BarChart3";
}
const steps: StepItem[] = [{
  number: "01",
  title: "Deep Technical & Search Intent Audit",
  stage: "Discovery & Analysis",
  description: "I personally inspect your technical crawlability, indexation health, site architecture, and current keyword footprints. I conduct a comprehensive 5-7 day SEO audit and deliver actionable, business-specific recommendations (not generic templates).",
  deliverables: "Bespoke Technical Audit, Indexing Action Plan, Search Intent Map",
  iconName: "Search"
}, {
  number: "02",
  title: "Tailored Growth Strategy & Prioritisation",
  stage: "Strategic Blueprint",
  description: "No cookie-cutter packages. I design a custom roadmap aligned directly with your commercial goals, whether that means dominating national queries, international markets, or high-intent B2B terms.",
  deliverables: "Quarterly SEO Roadmap, Competitor Gap Analysis, Revenue Keyword Target List",
  iconName: "Compass" as unknown as "Settings"
}, {
  number: "03",
  title: "Technical Remediation & On-Page Craft",
  stage: "Implementation & Build",
  description: "I work directly on your site structure, Core Web Vitals, schema markup, and content optimisation. You get exact, developer-ready instructions or direct hands-on fixes.",
  deliverables: "Structured Data Implementation, Content Optimisation Guides, Speed Enhancements",
  iconName: "Settings"
}, {
  number: "04",
  title: "Content Authority & AI Search Readiness",
  stage: "Modern Search Evolution",
  description: "I structure your site to earn organic prominence in standard Google results while ensuring your brand is clearly understood by modern AI-assisted search experiences.",
  deliverables: "Entity Authority Architecture, Information Gain Briefs, Topic Clusters",
  iconName: "Sparkles"
}, {
  number: "05",
  title: "Transparent Tracking & Continuous Iteration",
  stage: "Measurement & Refinement",
  description: "You receive direct monthly video walkthroughs and clean dashboards detailing qualified search growth, commercial rankings, and strategic adjustments. You retain full access to your own Google Search Console and analytics dashboard for complete reporting transparency — no black boxes.",
  deliverables: "Custom Looker Studio Dashboard, Monthly Video Debriefs, Ongoing Strategy Pivots",
  iconName: "BarChart3"
}];
export default function Methodology() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="seomethodologyprocess" data-nav="light" className="bg-background text-foreground py-20 md:py-28 lg:py-32 relative overflow-hidden">
      {/* Decorative background grid subtle overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_var(--color-secondary)_0%,_transparent_55%)] opacity-30 pointer-events-none" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 px-3.5 py-1 text-xs tracking-wider uppercase font-semibold text-primary border-primary/20 bg-secondary/50 rounded-full">
            How I Work
          </Badge>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold tracking-tight text-foreground mb-6">
            A Structured, First-Principles SEO Methodology
          </h2>
          
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            I don&apos;t hand off accounts to junior staff or rely on automated templates. 
            Every phase of my five-step framework is executed by me, designed to deliver sustainable 
            search visibility and direct commercial impact.
          </p>
        </div>

        {/* Timeline Process */}
        <motion.div className="max-w-5xl mx-auto relative" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-80px"
      }}>
          {/* Central Line for Desktop */}
          <div aria-hidden="true" className="hidden md:block absolute left-1/2 top-8 bottom-8 -translate-x-1/2 w-0.5 bg-gradient-to-b from-primary/30 via-accent/40 to-primary/30" />

          <div className="space-y-8 md:space-y-12">
            {steps.map((step, i) => {
            const isEven = i % 2 === 0;
            return <motion.div key={step.number} data-index={i} variants={itemVariants} className={`relative flex flex-col md:flex-row items-stretch ${isEven ? "md:flex-row-reverse" : ""}`}>
                  {/* Content Card Side */}
                  <div className="w-full md:w-1/2 md:px-6">
                    <Card className="h-full bg-card border-border/80 shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300 hover:border-primary/40 rounded-xl">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <span className="text-xs font-semibold uppercase tracking-wider text-accent">
                            {step.stage}
                          </span>
                          <span className="font-mono text-sm font-bold text-primary bg-primary/10 px-2.5 py-0.5 rounded-full">
                            Step {step.number}
                          </span>
                        </div>
                        <CardTitle className="text-xl sm:text-2xl font-serif font-semibold text-foreground">
                          {step.title}
                        </CardTitle>
                      </CardHeader>
                      
                      <CardContent className="space-y-4 pt-1">
                        <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                          {step.description}
                        </p>
                        
                        <div className="pt-3 border-t border-border">
                          <div className="text-xs font-semibold text-foreground flex items-center gap-1.5 mb-1.5">
                            <CheckCircle2 className="size-3.5 text-primary" />
                            <span>Core Deliverables:</span>
                          </div>
                          <p className="text-xs sm:text-sm text-muted-foreground">
                            {step.deliverables}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Center Node Indicator */}
                  <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 top-6 items-center justify-center">
                    <div className="size-10 rounded-full bg-background border-2 border-primary flex items-center justify-center shadow-xs">
                      <div className="size-3 rounded-full bg-accent" />
                    </div>
                  </div>

                  {/* Empty side for balanced spacing */}
                  <div className="hidden md:block w-1/2 md:px-6" />
                </motion.div>;
          })}
          </div>
        </motion.div>

        {/* Value Callout & Consultant Anchor */}
        <div className="mt-16 md:mt-24 max-w-4xl mx-auto">
          <div className="bg-secondary/40 border border-border rounded-2xl p-6 sm:p-8 lg:p-10 flex flex-col md:flex-row items-center justify-between gap-6 shadow-xs">
            <div className="space-y-2 text-center md:text-left">
              <div className="inline-flex items-center gap-2 text-primary font-semibold text-sm">
                <UserCheck className="size-4 text-primary" />
                <span>Direct Consultant Access</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-serif font-bold text-foreground">
                Want to learn more about my background?
              </h3>
              <p className="text-sm text-muted-foreground max-w-lg">
                Read about my personal journey, SEO philosophy, and why working directly with a senior independent consultant yields faster, clearer outcomes.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
              <Button asChild variant="outline" size="lg" className="rounded-lg font-medium">
                <Link to="/AboutMePage">
                  <span>About Me</span>
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>
              <Button asChild size="lg" className="rounded-lg font-medium bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/ContactPage">
                  <span>Book a Free Consultation</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Direct Working Principles Footer Pills */}
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 max-w-4xl mx-auto text-center">
          <div className="p-3 bg-card border border-border rounded-lg text-xs font-medium text-foreground flex items-center justify-center gap-2">
            <ShieldCheck className="size-4 text-primary shrink-0" />
            <span>No Long Lock-Ins</span>
          </div>
          <div className="p-3 bg-card border border-border rounded-lg text-xs font-medium text-foreground flex items-center justify-center gap-2">
            <ShieldCheck className="size-4 text-primary shrink-0" />
            <span>Direct Communication</span>
          </div>
          <div className="p-3 bg-card border border-border rounded-lg text-xs font-medium text-foreground flex items-center justify-center gap-2">
            <ShieldCheck className="size-4 text-primary shrink-0" />
            <span>Practical Implementation</span>
          </div>
          <div className="p-3 bg-card border border-border rounded-lg text-xs font-medium text-foreground flex items-center justify-center gap-2">
            <ShieldCheck className="size-4 text-primary shrink-0" />
            <span>Honest Measurement</span>
          </div>
        </div>

      </div>
    </section>;
}